import React from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import type { AllocationRuleDTO, AllocationType } from '../types';

const { Option } = Select;

const TRANSACTION_TYPES = [
  { value: 'SAVINGS_DEPOSIT', label: 'Savings Deposit' },
  { value: 'SAVINGS_WITHDRAWAL', label: 'Savings Withdrawal' },
  { value: 'SHARES_DEPOSIT', label: 'Shares Deposit' },
  { value: 'SHARES_WITHDRAWAL', label: 'Shares Withdrawal' },
  { value: 'LOAN_REPAYMENT', label: 'Loan Repayment' },
];

const ALLOCATION_TYPES: { value: AllocationType; label: string; hint: string }[] = [
  { value: 'FIXED', label: 'Fixed Amount', hint: 'Allocate an exact fixed amount' },
  { value: 'PERCENTAGE', label: 'Percentage', hint: 'Allocate a % of the row total' },
  { value: 'SEQUENTIAL', label: 'Sequential', hint: 'Pay up to the outstanding obligation' },
  { value: 'REMAINDER', label: 'Remainder', hint: 'Allocate whatever is left (use once, last rule)' },
];

interface Props {
  name: string | (string | number)[];
}

const AllocationRuleBuilder: React.FC<Props> = ({ name }) => {
  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => (
        <div className="space-y-3">
          {fields.map(({ key, name: fieldName, ...restField }, index) => (
            <div
              key={key}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3"
            >
              {/* Rule header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Rule {index + 1}</span>
                <Space size="small">
                  {index > 0 && (
                    <Tooltip title="Move up">
                      <Button
                        size="small"
                        icon={<HolderOutlined />}
                        onClick={() => move(index, index - 1)}
                      />
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(fieldName)}
                  />
                </Space>
              </div>

              {/* Hidden sequence field — kept in sync via index */}
              <Form.Item {...restField} name={[fieldName, 'sequence']} hidden initialValue={index + 1}>
                <InputNumber />
              </Form.Item>

              {/* Row 1: transaction type + allocation type */}
              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  {...restField}
                  name={[fieldName, 'transactionType']}
                  label="Transaction Type"
                  rules={[{ required: true, message: 'Required' }]}
                  className="mb-0"
                >
                  <Select placeholder="Select type">
                    {TRANSACTION_TYPES.map((t) => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[fieldName, 'allocationType']}
                  label="Allocation Type"
                  rules={[{ required: true, message: 'Required' }]}
                  className="mb-0"
                >
                  <Select placeholder="Select allocation">
                    {ALLOCATION_TYPES.map((t) => (
                      <Option key={t.value} value={t.value}>
                        <Tooltip title={t.hint}>{t.label}</Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {/* Row 2: amount fields (conditional) */}
              <Form.Item noStyle dependencies={[[...(Array.isArray(name) ? name : [name]), fieldName, 'allocationType']]}>
                {({ getFieldValue }) => {
                  const path = Array.isArray(name)
                    ? [...name, fieldName, 'allocationType']
                    : [name, fieldName, 'allocationType'];
                  const allocType: AllocationType = getFieldValue(path);

                  return (
                    <div className="grid grid-cols-2 gap-3">
                      {allocType === 'FIXED' && (
                        <Form.Item
                          {...restField}
                          name={[fieldName, 'fixedAmount']}
                          label="Fixed Amount"
                          rules={[{ required: true, message: 'Required' }]}
                          className="mb-0"
                        >
                          <InputNumber min={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      )}
                      {allocType === 'PERCENTAGE' && (
                        <Form.Item
                          {...restField}
                          name={[fieldName, 'percentage']}
                          label="Percentage (%)"
                          rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01, max: 100 }]}
                          className="mb-0"
                        >
                          <InputNumber min={0.01} max={100} step={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      )}
                    </div>
                  );
                }}
              </Form.Item>

              {/* Row 3: GL accounts */}
              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  {...restField}
                  name={[fieldName, 'drGlNo']}
                  label="DR GL No"
                  className="mb-0"
                >
                  <Input placeholder="e.g. 3100 or $MEMBER_GL" />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[fieldName, 'crGlNo']}
                  label="CR GL No"
                  className="mb-0"
                >
                  <Input placeholder="e.g. 1200 or $MEMBER_GL" />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  {...restField}
                  name={[fieldName, 'drAcNo']}
                  label="DR Account No"
                  className="mb-0"
                >
                  <Input placeholder="e.g. $MEMBER_AC or fixed ac no" />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[fieldName, 'crAcNo']}
                  label="CR Account No"
                  className="mb-0"
                >
                  <Input placeholder="e.g. $MEMBER_AC or fixed ac no" />
                </Form.Item>
              </div>

              {/* Row 4: trxCode + narration template */}
              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  {...restField}
                  name={[fieldName, 'trxCode']}
                  label="Transaction Code"
                  className="mb-0"
                >
                  <Input placeholder="e.g. SAV-DEP" />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[fieldName, 'narrationTemplate']}
                  label="Narration Template"
                  className="mb-0"
                >
                  <Input placeholder="{memberNo}, {amount}, {date}" />
                </Form.Item>
              </div>

              {/* Row 5: stopOnInsufficientBalance */}
              <Form.Item
                {...restField}
                name={[fieldName, 'stopOnInsufficientBalance']}
                label="Stop on Insufficient Balance"
                valuePropName="checked"
                className="mb-0"
              >
                <Switch />
              </Form.Item>
            </div>
          ))}

          <Button
            type="dashed"
            onClick={() => add({ sequence: fields.length + 1 })}
            icon={<PlusOutlined />}
            block
          >
            Add Allocation Rule
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export default AllocationRuleBuilder;
